import type { OverrideProperties, SetOptional } from 'type-fest';
import type { ValidationError } from './api.ts';
import type { DateType } from './date.ts';
import type { FrequencyCapping } from './frequencyCapping.ts';
import type { SegmentPartnerRevenueMethod, ThirdPartyFeeConfigRevenueMethod } from './gqlTypes.ts';
import type { ConstValues, WithRequired } from './helpers.ts';
import type { JsonString } from './json.ts';
import type { ListState } from './list.ts';
import type { BudgetMode, BudgetType, CadenceType, MonetizingRegion, PacingMode } from './monetizing.ts';
import type { PackageFormState, PackageWithTargeting, TargetingWithUrlTargeting } from './package.ts';
import type { Audience, AudiencePricing, AudienceStatus } from './targeting/audience.ts';
import type { I18nTranslate } from './translations.ts';

export type { FrequencyCapping } from './frequencyCapping.ts';

export type APIRboConfig = {
  hourly_target?: string | number | null;
  drop_percent?: string | number | null;
  channel?: string | null;
  goal?: string | null;
  target?: string | null;
  initialized_at?: string | null;
  message?: string | null;
  status?: string | null;
  threshold?: string | null;
  tactic?: string | null;
  custom_score_name?: string | null;
  mode?: string | null;
};
export type RboConfig = APIRboConfig | null;

export type PdpConfigApi = {
  media_spend_goal?: string | null;
  min_daily_avails?: number | string | null;
  min_win_rate?: string | null;
  period?: number | string | null;
  threshold?: string | null;
};

export type AmazonConfig = { budget: string | null };

export type RboInfo = {
  id: string;
  threshold: string;
};

export type GroupDeal = {
  id: string;
  name: string;
  rboConfig?: RboConfig;
  targetingWithUrlTargeting?: TargetingWithUrlTargeting;
  uid: string;
};

export interface DealCreate {
  account_executive?: string | null;
  account_manager?: string | null;
  account_uid: string;
  agency_salesforce_id?: string | null;
  brand_salesforce_id?: string | null;
  curated_deal_buyer_discount_enabled?: boolean;
  currency: string;
  deal_price: string;
  // use_market_floor           API Variance
  deal_source: string;
  // deal_participants          API Variance
  deal_supplements: SupplementalAdQuality | null;
  end_date?: string | null;
  fixed_cpm_enabled?: boolean | null;
  name?: string | null;
  package_uid: string;
  pmp_deal_type: PmpDealType;
  rbo_config?: RboConfig | null;
  start_date?: string;
  status: DealStatus | '' | string;
  third_party_fees_config?: null | [ThirdPartyFeeConfigApi];
}

export type ThirdPartyFeeConfig = OverrideProperties<
  ThirdPartyFeeConfigApi,
  {
    gross_cpm_cap: string;
    gross_share: string;
    partner_id: string | null;
    platform_share: string;
  }
>;

export type ThirdPartyFeeConfigApi = {
  gross_cpm_cap: string | null;
  gross_share: string | null;
  partner_id: string;
  platform_partner_id: string | null;
  platform_share: string | null;
  revenue_method: ThirdPartyFeeConfigRevenueMethod;
};

export type ThirdPartyFeeConfigApiPayload = SetOptional<ThirdPartyFeeConfigApi, 'platform_share'>;

export type ThirdPartyFee = {
  share?: string | null;
  cpm_cap?: string | null;
  fee_type: string;
  partner_id: string;
  revenue_method: ThirdPartyFeeConfigRevenueMethod;
};

export type ThirdPartyPartnersOption = { id: number; name: string; default_share: string | null };

export type DealError = {
  message: string;
  timestamp: string;
};

export interface DealBase extends DealCreate {
  account_id: string;
  account_uid: string;
  created_date?: string;
  synchronized_with?: SynchronizedWith | null;
  has_multiprovider_audience?: boolean;
  deal_id?: string | null;
  deal_source: string;
  enable_dv360_inventory_package?: boolean;
  expected_ad_category: string | null;
  frequency_capping: FrequencyCapping;
  id: string;
  instance_uid: string;
  is_pacing_enabled: boolean;
  is_rebate_disabled: boolean;
  lifetime_impression_goal: string | number | null;
  modified_date?: string;
  monetizing_id?: string | null;
  monetizing_uid?: string | null;
  name: string;
  open_auction_access: string | null;
  pmp_deal_type: PmpDealType;
  revision?: number;
  start_date?: string;
  third_party_fees?: ThirdPartyFee[] | null;
  type: 'deal';
  type_full: string;
  uid: string;
  error?: DealError | null;
}

export interface Deal extends DealBase {
  open_auction_access: string;
  use_market_floor: boolean | string | null;
  deal_participants: DealParticipant[];
}

export type EnrichedDeal = DealBase & {
  used_by_postauctiondiscount: boolean;
};

// Deal Form
export interface DealState extends Deal {
  budget_mode: BudgetMode | null;
  budget_type: BudgetType | null;
  cadence_type: CadenceType | null;
  curated_use_as_template?: boolean;
  curated_use_shared_targeting?: boolean;
  custom_deal_id?: string | null;
  deal_id_type_radio?: string | null;
  dv360_inventory_package?: DV360InventoryPackage | null;
  external_deal_id?: string | null;
  frequency_capping: FrequencyCapping;
  generated_deal_id?: string | null;
  generated_deal_id_prefix?: string | null;
  instance_uid: string;
  monetizing_budget: string;
  monetizing_region: MonetizingRegion | null;
  pacing_mode: PacingMode | null;
  segment_partner_revenue_method: SegmentPartnerRevenueMethodValues | null;
  segment_partner_platform_share?: Array<{ partner_id: string; share: string } | null> | null;
  synchronized_with?: SynchronizedWith | null;
  targeting_section_switch?: TargetingSectionDealFormSwitch;
  audience_status: AudienceStatus | null;
  pdp_config: PdpConfigApi | null;
  amazon_config?: AmazonConfig | null;
}

export type ListDealBase = Pick<
  Deal,
  | 'id'
  | 'uid'
  | 'name'
  | 'deal_id'
  | 'status'
  | 'start_date'
  | 'end_date'
  | 'modified_date'
  | 'created_date'
  | 'deal_participants'
  | 'error'
> & {
  synchronized_with?: SynchronizedWith | null;
  is_direct_deal?: boolean;

  // Metric fields (returned by deal_landing endpoint).
  // *_delta is a percentage rounded by the backend (23.2 = +23.2%), unlike the fractions used by Deal Insights.
  spend?: number | null;
  spend_delta?: number | null;
  impressions?: number | null;
  impressions_delta?: number | null;
  cpm?: number | null;
  cpm_delta?: number | null;
  publisher_revenue?: number | null;
  publisher_revenue_delta?: number | null;
  publisher_cpm?: number | null;
  publisher_cpm_delta?: number | null;
  partner_revenue?: number | null;
  partner_revenue_delta?: number | null;

  // Participant display fields (returned by deal_landing endpoint, response-only)
  dsp_names?: string[] | null;
  buyer_names?: string[] | null;

  // Health Score (returned by deal_landing endpoint, nullable raw 0-100)
  health_score?: number | null;
};

export type ListDeal = ListDealBase &
  Pick<
    Deal,
    'currency' | 'deal_price' | 'package_uid' | 'pmp_deal_type' | 'monetizing_uid' | 'lifetime_impression_goal'
  >;

export type OAListDeal = ListDealBase & OADealFields & { exchange_deal?: ListDealBase };

export interface OADealState extends DealState {
  targeting: PackageFormState;
}

export interface DealFormState<T extends Partial<Deal> = DealState> {
  loading: boolean;
  error?: ValidationError<T> | Error;
  currentDeal?: T;
}

type DealApiBase = {
  use_market_floor: '0' | '1' | null;
  deal_participants: DealParticipantApi[];
};

export interface DealApiCreate extends DealCreate, DealApiBase {}

export type DealApiMonetizing = {
  pacing_mode: string;
  budget_type: string;
  region: string;
  budget: string;
  flight_start: string;
  flight_end: string;
};

export interface DealApi
  extends Partial<DealApiMonetizing>,
    DealApiBase,
    WithRequired<
      DealBase,
      | 'modified_date'
      | 'account_executive'
      | 'account_manager'
      | 'revision'
      | 'end_date'
      | 'enable_dv360_inventory_package'
      | 'fixed_cpm_enabled'
      | 'frequency_capping'
    > {
  instance_uid: string;
  created_date: string;
  notes: string;
  apply_exchange_fee: string | null;
  has_multiple_publishers: string | null;
  segment_partner_revenue_method: SegmentPartnerRevenueMethodValues | null;
  segment_partner_platform_share?: Array<{ partner_id: string; share: string } | null> | null;
  price_config: string | null;
  bid_reuse_disabled: number;
  deal_exclusion_rate: string | null;
  account_id: string;
  package_id: string;
  package_name: string;
  package_obj?: PackageWithTargeting;
  v: number;
  deleted: number;
  type: 'deal';
  monetizing_uid: string | null;
  monetizing_id: string | null;
  is_pacing_enabled: boolean;
  expected_ad_category: string | null;
  agency_salesforce_id?: string | null;
  brand_salesforce_id?: string | null;
  old_ttd_sync_allowed?: boolean;
  pdp_config: PdpConfigApi | null;
  amazon_config?: AmazonConfig | null;
  amazon_do_sync?: boolean;
  buyer_account_id?: string | null;
  is_direct_deal?: boolean;
  publisher_name?: string | null;
}

export type DealApiSubmit = DealApiCreate & Partial<DealApi>;

// TODO: UIG-540 Fix DealParticipantApi is wrong type and uses of type casting
export interface DealParticipantApi {
  demand_partner: string;
  brand_ids?: Record<string, unknown>;
  buyer_id?: string | null;
  buyer_ids?: Record<string, unknown>;
  main_buyer_id: string | null;
  hasThirdPartyOptions?: boolean;
  amazon_sync_marketplace_enabled?: boolean;
}

export interface DealParticipant {
  demand_partner: string | null;
  brand_ids: string[];
  buyer_ids: string[];
  main_buyer_id: string | null;
  buyer_id: string | null;
  amazon_sync_marketplace_enabled?: boolean;
}

export interface SupplementalAdQualityFilter {
  op: 'allow' | 'allow_all' | null;
  ids: string[] | Record<string, boolean> | null;
}

export interface SupplementalAdQuality {
  allow_unbranded_rtb_bids?: '0' | '1';
  lean_filters: {
    disable_total_requests_filter: '0' | '1';
    disable_pixel_buckets: '0' | '1';
  };
  brand_labels: SupplementalAdQualityFilter | null;
  brands: SupplementalAdQualityFilter | null;
  content_attributes: SupplementalAdQualityFilter | null;
  creative_types: SupplementalAdQualityFilter | null;
  languages: SupplementalAdQualityFilter | null;
}

export const pmpDealType = {
  Guaranteed: '4',
  PreferredDeal: '1',
  PrivateAuction: '2',
  WithinOpenAuction: '3',
} as const;
export type PmpDealType = ConstValues<typeof pmpDealType>;

export const targetingSectionDealFormSwitch = {
  CURATED_PACKAGE: 2,
  EXISTING_PACKAGE: 1,
  UNIQUE_TARGETING: 0,
} as const;
export type TargetingSectionDealFormSwitch = ConstValues<typeof targetingSectionDealFormSwitch>;

export const onThelist = {
  Active: 'Active',
  Paused: 'Paused',
} as const;
export type OnThelist = ConstValues<typeof onThelist>;

export interface DV360InventoryPackage {
  description: string;
  on_the_list: OnThelist;
}

export interface SynchronizedWith {
  dv360_inventory?: {
    inventory_package_status: OnThelist;
    inventory_package_name: string;
  } | null;
  dv360seller?: DV360Seller | null;
  ttd_proposal_id?: string | number | null;
  pdp_sync?: {
    pdp_sync_status?: string;
    pdp_deal_negotiation_status?: string;
    pdp_deal_status?: string;
    pdp_last_sync_error?: {
      errors: JsonString[];
      error_datetime: string;
    };
  };
  amazon_sync?: {
    amazon_deal_id?: string;
    amazon_sync_status?: string;
    amazon_last_sync_timestamp?: string | null;
    amazon_last_sync_error?: {
      errors: Array<{ error_key: string; message: string }>;
      last_error_datetime: string;
    } | null;
  } | null;
}

export type DV360Seller = {
  order_name?: string | number | null;
  reject_reason?: string | null;
  failure_reason?: DV360FailureReason | null;
  reject_date?: string | null;
  failure_date?: string | null;
};

export const DV360FailureReasons = {
  ORDER_CANCELLED: 'order_cancelled',
} as const;
export type DV360FailureReason = ConstValues<typeof DV360FailureReasons>;

export const synchronizationStatuses = {
  FAILED: 2,
  NONE: -1,
  REJECTED: 1,
  SYNCED: 0,
} as const;
export type SynchronizationStatus = ConstValues<typeof synchronizationStatuses>;

export type OADealFields = {
  _package?: {
    audience_id: string | null;
    audience: Audience | null;
  };
  account?: {
    name: string;
  } | null;
  exchange_id?: string | null;
  exchange_uid?: string | null;
  pmp_deal_type?: PmpDealType | null;
};

export interface DealOaApi extends DealApi {
  _package?: {
    audience_id?: string | null;
    audience?: Audience | null;
  };
  account?: {
    name: string;
  } | null;
  exchange_uid: string;
}

export interface DealTroubleshoot {
  timestamp: string;
  stats: {
    bid_requests: number;
    bid_responses: number;
    impressions: number;
    bid_cpm?: number | null;
  };
}

export const oaDealStatus = {
  ACTIVE: 'Active',
  ERROR: 'Error',
  EXPIRED: 'Expired',
  PAUSED: 'Paused',
  PUBLISHING: 'Publishing',
  READY_FOR_ACTIVATION: 'ReadyForActivation',
} as const;
export type OaDealStatus = ConstValues<typeof oaDealStatus>;

export function getOaDealStatus(t: I18nTranslate) {
  return {
    Active: t('Active'),
    Error: t('Error'),
    Expired: t('Expired'),
    Paused: t('Paused'),
    Publishing: t('Publishing'),
  };
}

export const rboStatus = {
  ACTIVE: 'active',
  ERROR: 'error',
  INITIALIZING: 'initializing',
  PENDING: 'pending',
} as const;
export type RboStatus = ConstValues<typeof rboStatus>;

export function getRboStatus(t: I18nTranslate) {
  return {
    active: t('Engagement optimization live & optimizing deal'),
    error: t(
      'Engagement optimization could not be applied due to deal volume below minimum threshold. Reduce deal targeting to re-initialize the feature'
    ),
    initializing: t('Analyzing 3 hours of unfiltered deal traffic to apply Engagement optimization to deal'),
    pending: t('Queued for Engagement optimization activation'),
    ready: t('Engagement optimization initialization complete. The feature is pending deployment'),
  };
}

export const dealStatus = {
  Active: 'Active',
  Expired: 'Expired',
  GoalReached: 'Goal Reached',
  Paused: 'Paused',
  Publishing: 'Publishing',
  Error: 'Error',
} as const;
export type DealStatus = ConstValues<typeof dealStatus>;

export const dealStatusForm = {
  [dealStatus.Active]: 'active',
  [dealStatus.Expired]: 'inactive',
  [dealStatus.GoalReached]: 'goalreached',
  [dealStatus.Paused]: 'warning',
  [dealStatus.Publishing]: 'publishing',
  [dealStatus.Error]: 'error',
} as const;

export function getDealStatus(t: I18nTranslate) {
  return {
    Active: t('Active'),
    Error: t('Error'),
    Expired: t('Expired'),
    GoalReached: t('Goal Reached'),
    Paused: t('Paused'),
    Publishing: t('Publishing'),
  };
}

export type DealsListFiltersValue = {
  deal_id?: string;
  exchange_deal_id?: string;
  phrase: string;
  status?: string;
  createdBy?: string;
  account?: string;
  demand_partner?: string;
  buyer_ids?: string;
  pmp_deal_type?: string;
  package_uid?: string;
  spend_min?: number;
  spend_max?: number;
  impressions_min?: number;
  impressions_max?: number;
  cpm_min?: number;
  cpm_max?: number;
  publisher_revenue_min?: number;
  publisher_revenue_max?: number;
  publisher_cpm_min?: number;
  publisher_cpm_max?: number;
  partner_revenue_min?: number;
  partner_revenue_max?: number;
  health_score_min?: number;
  health_score_max?: number;
};

export type DealsListState<T extends Partial<Deal> = ListDeal> = ListState<T, DealsListFiltersValue>;

export const dealDetailsTabs = {
  AMAZON_SYNC: 'amazon-sync',
  COMMENTS: 'comments',
  DETAILS: 'details',
  HISTORY: 'history',
  TROUBLESHOOT: 'troubleshoot',
  TTD_SYNC: 'ttd-sync',
} as const;
export type DealDetailsTabs = ConstValues<typeof dealDetailsTabs>;

export type SegmentPartnerRevenueMethodValues = ConstValues<typeof SegmentPartnerRevenueMethod>;

export const dealFormField = {
  AGENCY_SALESFORCE_ID: 'agency_salesforce_id',
  BRAND_SALESFORCE_ID: 'brand_salesforce_id',
  ACCOUNT_EXECUTIVE: 'account_executive',
  ACCOUNT_MANAGER: 'account_manager',
  ACCOUNT_UID: 'account_uid',
  BUDGET_TYPE: 'budget_type',
  BUDGET_MODE: 'budget_mode',
  CURRENCY: 'currency',
  CUSTOM_DEAL_ID: 'custom_deal_id',
  DEAL_ID: 'deal_id',
  DEAL_ID_TYPE_RADIO: 'deal_id_type_radio',
  DEAL_PARTICIPANTS: 'deal_participants',
  DEAL_PRICE: 'deal_price',
  DEAL_SOURCE: 'deal_source',
  DEAL_SUPPLEMENTS: 'deal_supplements',
  ENABLE_DV360_INVENTORY_PACKAGE: 'enable_dv360_inventory_package',
  END_DATE: 'end_date',
  EXPECTED_AD_CATEGORY: 'expected_ad_category',
  EXTERNAL_DEAL_ID: 'external_deal_id',
  FIXED_CPM_ENABLED: 'fixed_cpm_enabled',
  FREQUENCY_CAPPING: 'frequency_capping',
  GENERATED_DEAL_ID_PREFIX: 'generated_deal_id_prefix',
  IMPRESSION_GOAL: 'lifetime_impression_goal',
  IS_DISCOUNT_ENABLED: 'curated_deal_buyer_discount_enabled',
  IS_PACING_ENABLED: 'is_pacing_enabled',
  HAS_MULTIPROVIDER_AUDIENCE: 'has_multiprovider_audience',
  PRICING_BY_ORG_DATA: 'pricing_by_org_data',
  CONTEXTUAL_MAX_POM: 'contextual_max_pom',
  SEGMENT_PARTNER_REVENUE_METHOD: 'segment_partner_revenue_method',
  SEGMENT_PARTNER_PLATFORM_SHARE: 'segment_partner_platform_share',
  MONETIZING_BUDGET: 'monetizing_budget',
  MONETIZING_REGION: 'monetizing_region',
  MONETIZING_UID: 'monetizing_uid',
  MONETIZING_CADENCE_UNIT: 'cadence_type',
  NAME: 'name',
  OPEN_AUCTION_ACCESS: 'open_auction_access',
  PACING_MODE: 'pacing_mode',
  PACKAGE_ID: 'package_id',
  PACKAGE_UID: 'package_uid',
  PARTNER_IDS: 'partner_account_uids',
  PMP_DEAL_TYPE: 'pmp_deal_type',
  PREVIEW_DEAL_ID: 'preview_deal_id',
  RBO_CONFIG: 'rbo_config',
  START_DATE: 'start_date',
  STATUS: 'status',
  TARGETING: 'targeting',
  USE_MARKET_FLOOR: 'use_market_floor',
  UID: 'uid',
  METADATA: '__meta',
  METADATA_GROUP_DEAL: '__meta.group_deal',
  PDP_CONFIG: 'pdp_config',
  AMAZON_CONFIG: 'amazon_config',
  BUYER_ACCOUNT_ID: 'buyer_account_id',
} as const;
export type DealFormField = ConstValues<typeof dealFormField>;

export const RETARGETING_AQUA_VALIDATION_ERROR = 'retargetingAquaValidationError';

/** Deal participants scroll anchor. */
export const DEAL_PARTICIPANTS_SECTION_ID = 'deal-participants-section';

/** Curated Deal Fees for Enabled Data Partners scroll anchor. */
export const ENABLED_DATA_PARTNERS_SECTION_ID = 'enabled-data-partners';

export const rboAlertKind = {
  GROUP_VALIDATION: 'groupValidation',
  APPLY: 'apply',
  DISABLE: 'disable',
} as const;

export type RBOAlertKind = ConstValues<typeof rboAlertKind>;

export const rboConfigField = {
  DROP_PERCENT: 'drop_percent',
  HOURLY_TARGET: 'hourly_target',
  TARGET: 'target',
  MODEL_GOAL: 'goal',
  CHANNEL: 'channel',
  TACTIC: 'tactic',
  THRESHOLD: 'threshold',
  CUSTOM_SCORE_NAME: 'custom_score_name',
  MODE: 'mode',
} as const;
export type RboConfigField = ConstValues<typeof rboConfigField>;

export const rboConfigMode = {
  BOOST: 'boost',
  MAX: 'max',
  CUSTOM: 'custom',
} as const;
export type RboConfigMode = ConstValues<typeof rboConfigMode>;

export const RBO_SUPPORTED_FORMATS = ['video', 'banner'] as const;
export type RboSupportedFormat = (typeof RBO_SUPPORTED_FORMATS)[number];

export interface DealFormData
  extends Partial<
    Omit<
      DealState,
      | 'account_uid'
      | 'currency'
      | 'deal_participants'
      | 'deal_source'
      | 'end_date'
      | 'package_uid'
      | 'pmp_deal_type'
      | 'start_date'
      | 'third_party_fees_config'
      | 'segment_partner_revenue_method'
    >
  > {
  account_uid: string | null;
  currency: string | null;
  custom_deal_id?: string | null;
  deal_id_type?: string | null;
  deal_participants: DealParticipant[];
  deal_source: string | null;
  end_date?: Date | null;
  external_deal_id?: string | null;
  generated_deal_id_prefix?: string | null;
  package_uid: string | null;
  package_id?: string | null;
  pmp_deal_type: PmpDealType | null;
  start_date?: Date | null;
  agency_salesforce_id?: string | null;
  brand_salesforce_id?: string | null;
  curated_deal_buyer_discount_enabled?: boolean;
  third_party_fees_config: ThirdPartyFeeConfig[];
  third_party_fees_config_enabled: boolean;
  segment_partner_revenue_method: SegmentPartnerRevenueMethodValues | null;
  segment_partner_platform_share?: Array<{ partner_id: string; share: string } | null> | null;
  pricing_by_org_data?: AudiencePricing['pricing_by_org'] | null;
  contextual_max_pom?: number | null;
  old_ttd_sync_allowed?: boolean;
  pdp_do_sync?: boolean;
  pdp_config?: PdpConfigApi | null;
  amazon_config?: AmazonConfig | null;
  buyer_account_id?: string | null;
}

export type DealFormDataWithTargeting<D extends DateType = Date | null> = Omit<
  DealFormData,
  'start_date' | 'end_date'
> & {
  // TODO: UIG-917: https://openxtechinc.atlassian.net/browse/UIG-917
  targeting: PackageFormState;
  start_date?: D;
  end_date?: D;
  __meta: {
    group_deal?: GroupDeal[] | null;
  };
};

export type DealsTableFetchOptions = {
  accountsLoading?: boolean;
  selectedAccountId?: string;
};

export const dealSource = {
  BuyerSourced: 'Buyer Sourced',
  OxBuyerDevSourced: 'OpenX - Buyer Dev Sourced',
  OxDspSourced: 'OpenX - DSP Sourced',
  Publisher: 'Publisher',
  TechPartnerSourced: 'Tech Partner Sourced',
} as const;
export type DealSource = ConstValues<typeof dealSource>;

export type DealMetaValue = {
  group_deal?: GroupDeal[];
};
